import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type DigitalAssetModel from './digital-asset.ts';
import type { DocumentType } from '../types/tuition-assistance.ts';

interface IdentityCheck {
  detected_name: string | null;
  confidence: number;
}

export default class AssetSummaryModel extends Model {
  // Basic attributes
  @attr declare assetType: string;
  @attr declare createdBy: Record<string, unknown>;
  @attr declare etag: string;
  @attr declare quickSummary: Record<string, unknown>;
  @attr declare summary: Record<string, unknown>;
  @attr declare updatedBy: Record<string, unknown>;
  @attr declare createdAt: string;
  @attr declare updatedAt: string;

  // Relationships
  @belongsTo('digital-asset', { async: false, inverse: 'assetSummary', polymorphic: true })
  declare digitalAsset: DigitalAssetModel;

  // Computed properties
  get hasSummary(): boolean {
    return Boolean(this.summary && Object.keys(this.summary).length > 0);
  }

  get hasQuickSummary(): boolean {
    return Boolean(this.quickSummary && Object.keys(this.quickSummary).length > 0);
  }

  /**
   * Categories the AI found in this document. Always at least one element when
   * the AI has run; empty array if the summary has not yet populated.
   * @see ATTACHMENT_FEEDBACK_REALTIME_VALIDATION.md
   */
  get detectedDocumentTypes(): DocumentType[] {
    const v = this.summary?.['detected_document_types'];
    return Array.isArray(v) ? (v as DocumentType[]) : [];
  }

  get identityCheck(): IdentityCheck {
    const v = this.summary?.['identity_check'] as IdentityCheck | undefined;
    return v ?? { detected_name: null, confidence: 0 };
  }

  get confidenceScore(): number {
    const v = this.summary?.['confidence_score'];
    return typeof v === 'number' ? v : 0;
  }

  /**
   * Does this summary's classification satisfy the given required type? `grade`
   * is treated as a subset of `transcript`.
   */
  satisfiesType(required: DocumentType): boolean {
    const detected = this.detectedDocumentTypes;
    if (detected.includes(required)) return true;
    if (required === 'grade' && detected.includes('transcript')) return true;
    return false;
  }

  [Type] = 'asset-summary' as const;
}
